import React, { forwardRef } from 'react';
import { Fab as MuiFab } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 * This Component is a wrapper on MUI component.
 * This component should be imported and not MUI directly!
 *
 * Explicit Fab props based on actual usage in the codebase. Wraps the MUI v5
 * (`@mui/material`) component so the MUI version lives in one place.
 */
export interface FabProps {
  /** CSS class name to apply to the root element. */
  className?: string;
  /** Callback fired when the button is clicked. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Accessible label describing the floating action button. */
  'aria-label'?: string;
  /** The content of the button (typically an icon). */
  children?: React.ReactNode;
}

const Fab = forwardRef<HTMLButtonElement, FabProps>(
  ({ className, onClick, 'aria-label': ariaLabel, children, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Fab: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the FabProps interface and to the component's signature.`,
        true
      );
    }
    return (
      <MuiFab className={className} onClick={onClick} aria-label={ariaLabel} ref={ref} {...restProps}>
        {children}
      </MuiFab>
    );
  }
);

Fab.displayName = 'Fab';

export default Fab;
